import React, { useEffect, useState } from 'react';
import { DeleteJson, GetJson, PostJson, PutJson } from '@awp/helpers/fetchReact';
import { ENTITY_TITLES, FORM_MODE, Permissions, REST_API } from '@awp/constants';
import { Card, Col, Container, Row } from 'react-bootstrap';
import { FormInput, FormTextArea } from '@awp/components/Common/AwpFormInputs';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import AwpYesNoDialog from '@awp/components/Common/AwpYesNoDialog';
import AwpFormActionButtons from '@awp/components/Common/AwpFormActionButtons';
import AwpSpinner from '@awp/components/Common/AwpSpinner';
import { getCrud } from '@awp/shared/Crud';
import { isPristine } from '@awp/helpers/UIHelper';

const ManufacturerEdit = ({ setMode, mode, manufacturerId, data, setData, setLoading, loading }) => {
  const [manufacturerData, setManufacturerData] = useState({});
  const [entityErrors, setEntityErrors] = useState(null);
  const [initialDto, setInitialDto] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const crud = getCrud(ENTITY_TITLES.MANUFACTURER, mode);
  const { title, readOnly, successMessage, buttonLabel, entityName } = crud;

  const mapDTO = (x) => {
    return {
      name: x.Name ?? '',
      note: x.Note ?? '',
    };
  };
  useEffect(() => {
    if (mode === FORM_MODE.CREATE) {
      setManufacturerData({ Name: '', Note: '' });
      setInitialDto(mapDTO(manufacturerData));
      return;
    }

    setLoading(true);
    const url = REST_API.MANUFACTURERS + manufacturerId;

    GetJson(url)
      .then((response) => response.json())
      .then((manu) => {
        setManufacturerData(manu);
        setInitialDto(mapDTO(manu));
        setLoading(false);
      });
  }, []);

  const updateManufacturer = (setSubmitting, resetForm, setErrors, manufacturerDto) => {
    const url = REST_API.MANUFACTURERS + manufacturerDto.Id;

    PutJson(url, manufacturerDto)
      .then(async (response) => {
        if (response.ok) {
          toast.success(successMessage);
          const manu = await response.json();
          setManufacturerData(manu);
          updateData(manu);
          setInitialDto(mapDTO(manu));
          resetForm({
            values: {
              name: manu.Name,
              note: manu.Note,
            },
          });
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEntityErrors({ Manufacturer: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const createManufacturer = (setSubmitting, setErrors, manufacturerDto) => {
    const url = REST_API.MANUFACTURERS;

    PostJson(url, manufacturerDto)
      .then(async (response) => {
        if (response.ok) {
          const manufacturer = await response.json();
          setInitialDto(mapDTO(manufacturer));
          setManufacturerData(manufacturer);
          updateData(manufacturer);
          toast.success(successMessage);
          setMode(FORM_MODE.VIEW);
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEntityErrors({ Manufacturer: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  const deleteManufacturer = () => {
    const id = manufacturerData.Id;
    const url = REST_API.MANUFACTURERS + id;

    DeleteJson(url, {})
      .then(async (response) => {
        if (response.ok) {
          setManufacturerData({});
          removeData(id);
          toast.success(`${entityName} has been deleted`);
          setMode(FORM_MODE.LIST);
        } else if (response.status === 412) {
          const errors = await response.json();
          //setErrors(errors);
          if (errors.entity) setEntityErrors({ Manufacturer: errors.entity });
        }
      })
      .finally(() => {
        //setSubmitting(false);
      });
  };
  const handleSubmit = (values, { setSubmitting, resetForm, setErrors }) => {
    setSubmitting(true);

    const manufacturerDto = {
      ...manufacturerData,
      Name: values.name,
      Note: values.note,
    };

    if (mode === FORM_MODE.EDIT) {
      updateManufacturer(setSubmitting, resetForm, setErrors, manufacturerDto);
    } else {
      createManufacturer(setSubmitting, setErrors, manufacturerDto);
    }
  };

  const updateData = (manu) => {
    if (mode === FORM_MODE.EDIT) {
      const index = data.findIndex((x) => x.Id === manu.Id);
      if (index < 0) return;
      data[index] = manu;
      setData([...data]);
    } else {
      setData([...data, manu]);
    }
  };
  const removeData = (Id) => {
    if (mode === FORM_MODE.EDIT) {
      let _data = [...data];
      let _removedData = [];
      _data.map((o) => {
        if (o.Id !== Id) {
          _removedData.push(o);
        }
      });
      setData([..._removedData]);
    }
  };
  const onClickDelete = () => {
    setShowDeleteConfirm(true);
  };

  return (
    <>
      {loading ? (
        <Container>
          <AwpSpinner />
        </Container>
      ) : (
        <>
          <Formik
            initialValues={{
              name: manufacturerData.Name ?? '',
              note: manufacturerData.Note ?? '',
            }}
            validateOnMount={true}
            validationSchema={Yup.object({
              name: Yup.string()
                //.matches(/^[a-z0-9 ]+$/i, 'Special characters are not allowed')
                .max(50, 'Maximum 50 characters are allowed')
                .required('Name is a required field'),
              note: Yup.string().max(2000, 'Maximum 2000 characters are allowed'),
            })}
            onSubmit={handleSubmit}
          >
            {(props) => (
              <Form>
                <span className="awp-text-btn" onClick={() => setMode('list')}>
                  List Manufacturers
                </span>
                <span className="mx-3">&gt;</span>
                <span>{title}</span>
                <Card border="light" className="page-card">
                  <Card.Header>
                    <Row className="mt-4"></Row>
                    {crud.getHeader()}
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col sm={6} className="mb-4">
                        <FormInput
                          label="Manufacturer Name"
                          instruction="(50 Characters max)"
                          readOnly={readOnly || manufacturerData.IsProtected === 'Y'}
                          name="name"
                          id="name"
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col sm={8} className="mb-4">
                        <FormTextArea
                          readOnly={readOnly}
                          label="Notes"
                          instruction="(2,000 Characters max)"
                          id="note"
                          name="note"
                          rows={3}
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                  <Card.Footer>
                    {!showDeleteConfirm ? (
                      <Row className="my-2">
                        <AwpFormActionButtons
                          title=" "
                          entityName={entityName}
                          buttonLabel={buttonLabel}
                          mode={mode}
                          setMode={setMode}
                          onClickDelete={onClickDelete}
                          permissions={[Permissions.WriteManufacturer]}
                          readOnly={readOnly}
                          isAlignLeft={true}
                          btnCancelName={'Close'}
                          props={props}
                          isPristine={isPristine(props.values, initialDto)}
                        />
                      </Row>
                    ) : (
                      <Row className="my-2">
                        <AwpYesNoDialog
                          action={deleteManufacturer}
                          setShowYesNoConfirm={setShowDeleteConfirm}
                          props={props}
                        />
                      </Row>
                    )}
                  </Card.Footer>
                </Card>
              </Form>
            )}
          </Formik>
          <AwpValidationErrorModal setErrors={setEntityErrors} errors={entityErrors} />
        </>
      )}
    </>
  );
};

export default ManufacturerEdit;
